import React, { ReactElement } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Eyebrow, Icon, Checkbox, IconSize } from '@atlassian/learning-components';
import Badge from '@atlaskit/badge';

export interface FilterOption {
  label: string;
  count?: number; // Optional count badge
}

interface FilterSectionProps {
  title: string;
  queryParamKey: string;
  options: readonly (string | FilterOption)[]; // Support both string and FilterOption
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedValues?: Set<string>; // Currently selected values (for local state)
  onFilterToggle?: (value: string) => void; // Callback when filter is toggled
}

export const FilterSection = ({
  title,
  queryParamKey,
  options,
  isExpanded,
  onToggleExpand,
  selectedValues,
  onFilterToggle,
}: FilterSectionProps): ReactElement => {
  return (
    <div css={sectionContainerStyles}>
      <div css={expanderContainerStyles} onClick={onToggleExpand} data-testid={`filter-section-${queryParamKey}`}>
        <Icon size={IconSize.LARGE} type={isExpanded ? 'chevron-down' : 'chevron-right'} />
        <Eyebrow size="medium" fontWeight="semibold">
          {title.toUpperCase()}
        </Eyebrow>
      </div>
      {isExpanded && (
        <div css={filtersListStyles}>
          {options.map((option, index) => {
            // Support both string and FilterOption format
            const label = typeof option === 'string' ? option : option.label;
            const count = typeof option === 'string' ? undefined : option.count;
            const isChecked = selectedValues?.has(label) || false;

            return (
              <div
                key={label}
                aria-label={`${queryParamKey} filter checkbox`}
                css={[filterContainerStyles, index === 0 && firstFilterStyles]}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.code === 'Space') {
                    onFilterToggle?.(label);
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                tabIndex={0}
              >
                <Checkbox
                  isChecked={isChecked}
                  onChange={() => onFilterToggle?.(label)}
                  label={<span css={labelStyles}>{label}</span>}
                  data-testid={`${queryParamKey}-filter`}
                  tabIndex={-1}
                />
                {count !== undefined && (
                  <div data-testid="filter-badge" css={badgeContainerStyles}>
                    <Badge>{count}</Badge>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const sectionContainerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  paddingTop: token('space.200'),
  marginBottom: token('space.200'),
  borderTop: `2px solid ${token('color.border')}`,
  overflow: 'hidden',
});

const expanderContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',

  // Eyebrow margin override
  '& > h4': {
    marginLeft: token('space.100'),
    marginTop: 0,
    marginBottom: 0,
  },
});

const filtersListStyles = css({
  marginLeft: '40px',
  width: '100%',
  gap: token('space.300'),
  display: 'flex',
  flexDirection: 'column',
});

const filterContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 0 4px 0',

  label: {
    whiteSpace: 'nowrap',
  },
});

const firstFilterStyles = css({
  marginTop: token('space.100'),
});

const labelStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const badgeContainerStyles = css({
  display: 'flex',
  flexWrap: 'nowrap',
  marginLeft: '6px',
  width: '50px',
});
